import { SupabaseClient } from '@supabase/supabase-js';
import { DataServiceError, ErrorCodes } from '@/services/dataService';
import type {
  CompanyNotificationSettings,
  NotificationPreferences,
  TransporterNotificationStatus,
} from '@/types/notification';

/**
 * 陸送員への通知設定・送信を管理するサービスクラス
 *
 * 責務:
 * - 会社ごとの通知設定CRUD操作
 * - 一斉通知送信（翌日スケジュール、緊急連絡等）
 * - 陸送員ごとの通知設定取得
 * - 通知履歴管理
 *
 * 依存関係:
 * - SupabaseClient（データベースアクセス）
 * - emailService.ts（実際のメール送信）
 */
export class SupabaseNotificationService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * 会社の通知設定を取得
   *
   * @param companyId - 会社ID
   * @returns 通知設定（存在しない場合はデフォルト設定を自動作成）
   * @throws DataServiceError - データ取得失敗時
   */
  async getCompanyNotificationSettings(companyId: string): Promise<CompanyNotificationSettings> {
    try {
      const { data, error } = await this.supabase
        .from('company_notification_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = データが存在しない（許容エラー）
        throw new DataServiceError(
          '通知設定の取得に失敗しました',
          ErrorCodes.DATA_FETCH_FAILED,
          error
        );
      }

      // データが存在しない場合はデフォルト設定を作成
      if (!data) {
        return this.createDefaultNotificationSettings(companyId);
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) throw error;
      throw new DataServiceError(
        '通知設定の取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  /**
   * 会社の通知設定を更新
   *
   * @param companyId - 会社ID
   * @param settings - 更新する通知設定
   * @returns 更新後の通知設定
   * @throws DataServiceError - データ更新失敗時
   */
  async updateCompanyNotificationSettings(
    companyId: string,
    settings: Partial<NotificationPreferences>
  ): Promise<CompanyNotificationSettings> {
    try {
      const { data, error } = await this.supabase
        .from('company_notification_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString(),
        })
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          '通知設定の更新に失敗しました',
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) throw error;
      throw new DataServiceError(
        '通知設定の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * デフォルト通知設定を作成
   *
   * @param companyId - 会社ID
   * @returns 作成された通知設定
   * @throws DataServiceError - データ作成失敗時
   */
  private async createDefaultNotificationSettings(
    companyId: string
  ): Promise<CompanyNotificationSettings> {
    try {
      const { data, error } = await this.supabase
        .from('company_notification_settings')
        .insert({
          company_id: companyId,
          notify_on_schedule_assignment: true,
          notify_on_schedule_change: true,
          notify_on_schedule_cancellation: true,
          daily_schedule_notification_time: '18:00:00',
          enable_daily_schedule_notification: true,
          notification_methods: ['email'],
        })
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          'デフォルト通知設定の作成に失敗しました',
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) throw error;
      throw new DataServiceError(
        'デフォルト通知設定の作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 通知対象の陸送員リストを取得
   *
   * @param companyId - 会社ID
   * @param filters - フィルター条件（オプション）
   * @returns 通知可能な陸送員リスト
   * @throws DataServiceError - データ取得失敗時
   */
  async getNotifiableTransporters(
    companyId: string,
    filters?: {
      receiveEmailNotifications?: boolean;
      status?: 'active' | 'inactive';
    }
  ): Promise<TransporterNotificationStatus[]> {
    try {
      let query = this.supabase
        .from('company_land_transporters')
        .select('id, name, email, receive_email_notifications, status')
        .eq('company_id', companyId)
        .not('email', 'is', null);

      // フィルター適用
      if (filters?.receiveEmailNotifications !== undefined) {
        query = query.eq('receive_email_notifications', filters.receiveEmailNotifications);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) {
        throw new DataServiceError(
          '陸送員リストの取得に失敗しました',
          ErrorCodes.DATA_FETCH_FAILED,
          error
        );
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) throw error;
      throw new DataServiceError(
        '陸送員リストの取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  /**
   * 陸送員のメール通知設定を更新
   *
   * @param transporterId - 陸送員ID
   * @param receiveEmailNotifications - メール通知受信設定
   * @returns 更新成功フラグ
   * @throws DataServiceError - データ更新失敗時
   */
  async updateTransporterEmailPreference(
    transporterId: string,
    receiveEmailNotifications: boolean
  ): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('company_land_transporters')
        .update({
          receive_email_notifications: receiveEmailNotifications,
          updated_at: new Date().toISOString(),
        })
        .eq('id', transporterId);

      if (error) {
        throw new DataServiceError(
          '陸送員の通知設定更新に失敗しました',
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      return true;
    } catch (error) {
      if (error instanceof DataServiceError) throw error;
      throw new DataServiceError(
        '陸送員の通知設定更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }
}
